import * as Sentry from '@sentry/react'

import { getAppEnv } from '@/core/config/env'
import type { UserProfile } from '@/core/auth/session/session.types'

const SENSITIVE_FIELDS = new Set(['password', 'code', 'otp', 'documentNumber'])

const REPLAY_DISABLED_PATHS = ['/login', '/forgot-password', '/register']

// Flows that map a route prefix to a human-readable name used as Sentry transaction tag.
export const FLOW_NAMES: Record<string, string> = {
  '/register': 'registration',
  '/login': 'login',
  '/forgot-password': 'password-reset',
  '/dashboard': 'dashboard',
}

function isReplayDisabledPath(path: string): boolean {
  return REPLAY_DISABLED_PATHS.some((p) => path.startsWith(p))
}

function scrubSensitiveData(obj: unknown): unknown {
  if (!obj || typeof obj !== 'object') return obj
  if (Array.isArray(obj)) return obj.map(scrubSensitiveData)
  return Object.fromEntries(
    Object.entries(obj as Record<string, unknown>).map(([key, value]) => [
      key,
      SENSITIVE_FIELDS.has(key) ? '[Filtered]' : scrubSensitiveData(value),
    ]),
  )
}

function tracesSampleRateFor(env: string): number {
  if (env === 'production') return 0.2
  return 1.0  // development and qa: capture everything
}

function replaysSessionRateFor(env: string): number {
  if (env === 'development') return 0.0
  if (env === 'qa') return 1.0
  return 0.1
}

let isInitialized = false

export function initializeMonitoring() {
  const { sentryDsn, appEnv, appVersion, apiGatewayUrl } = getAppEnv()

  if (isInitialized || !sentryDsn) return

  Sentry.init({
    dsn: sentryDsn,
    environment: appEnv,
    release: appVersion,
    sendDefaultPii: false,

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    tracesSampleRate: tracesSampleRateFor(appEnv),
    replaysSessionSampleRate: replaysSessionRateFor(appEnv),
    replaysOnErrorSampleRate: 1.0,

    // Propagate trace headers to the BFF so errors correlate end-to-end.
    tracePropagationTargets: ['localhost', apiGatewayUrl],

    beforeSend(event, hint) {
      const error = hint.originalException
      if (error instanceof Error && error.name === 'CanceledError') return null

      const currentPath = window.location.pathname
      if (isReplayDisabledPath(currentPath)) {
        if (event.type === 'replay_event') return null
      }

      if (event.request?.data) {
        event.request.data = scrubSensitiveData(event.request.data)
      }

      return event
    },
  })

  isInitialized = true
}

export function sentryReactErrorHandler(
  callback?: Parameters<typeof Sentry.reactErrorHandler>[0],
) {
  return Sentry.reactErrorHandler(callback)
}

export function captureRequestError(error: unknown) {
  Sentry.captureException(error)
}

export function setMonitoringUser(user: UserProfile | null) {
  if (!user) {
    Sentry.setUser(null)
    return
  }
  Sentry.setUser({
    id: user.contactId,
    email: user.email,
    username: `${user.firstName} ${user.lastName}`,
  })
}

/**
 * Add a breadcrumb to trace a step within a named user flow.
 * Call this at each meaningful step (e.g. "identity-verified", "otp-sent").
 */
export function captureFlowStep(flow: string, step: string, data?: Record<string, string>) {
  Sentry.addBreadcrumb({
    category: `flow.${flow}`,
    message: step,
    data,
    level: 'info',
  })
}

/**
 * Record a flow-level error with full context so Sentry groups it by flow + step.
 */
export function captureFlowError(
  flow: string,
  step: string,
  error: unknown,
  extra?: Record<string, string>,
) {
  Sentry.withScope((scope) => {
    scope.setTag('flow', flow)
    scope.setTag('flow.step', step)
    if (extra) scope.setContext('flow_context', extra)
    Sentry.captureException(error)
  })
}
